import { lazy, Suspense, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Input,
    Link,
    NativeSelect,
    Spinner,
    Stack,
    Switch,
    Text,
    Textarea,
} from '@chakra-ui/react';
import { FiCheck, FiExternalLink, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import { useColorModeValue } from '../components/ui/color-mode';
import {
    getCustomMetricSuggestions,
    previewCustomMetricSuggestion,
    reviewCustomMetricSuggestion,
    scanCustomMetricSuggestions,
} from '../api';
import { useAuth0 } from '@auth0/auth0-react';
import { getLoginRedirectOptions, isRecoverableAuthError } from '../utils/auth0Errors';

const MetricPreviewRechartsChart = lazy(() =>
    import('recharts').then(({
        CartesianGrid,
        Line,
        LineChart,
        ResponsiveContainer,
        Tooltip,
        XAxis,
        YAxis,
    }) => ({
        default: function MetricPreviewRechartsChart({
            chartData,
            gridStroke,
            tooltipBg,
            tooltipBorder,
            unit,
        }) {
            return (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} minTickGap={20} />
                        <YAxis
                            tick={{ fontSize: 12 }}
                            width={90}
                            tickFormatter={(value) => formatValue(value, unit)}
                        />
                        <Tooltip
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;
                                const point = payload[0]?.payload || {};
                                return (
                                    <Stack
                                        gap={1}
                                        bg={tooltipBg}
                                        borderWidth="1px"
                                        borderColor={tooltipBorder}
                                        borderRadius="md"
                                        p={3}
                                    >
                                        <Text fontWeight="semibold">{formatDate(label)}</Text>
                                        <Text fontSize="sm">{formatValue(payload[0].value, unit)}</Text>
                                        {point.filingDate && (
                                            <Text fontSize="xs" color="gray.500">
                                                Filed {formatDate(point.filingDate)}
                                            </Text>
                                        )}
                                    </Stack>
                                );
                            }}
                        />
                        <Line type="monotone" dataKey="value" stroke="#2C7A7B" strokeWidth={2} dot />
                    </LineChart>
                </ResponsiveContainer>
            );
        },
    })),
);

const initialState = {
    suggestions: [],
    suggestionsTicker: '',
    status: 'idle',
    scanStatus: 'idle',
    reviewStatusByKey: {},
    previewStatusByKey: {},
    previewsByKey: {},
    error: null,
    lastScan: null,
};

const PREVIEW_POLL_DELAY_MS = 2500;
const PREVIEW_POLL_ATTEMPTS = 24;
const DEFAULT_TICKER = 'HOOD';
const VALUE_FORMATTER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 });
const PREVIEW_DEFINITION_FIELDS = new Set(['display_name', 'metric_key', 'unit', 'row_label', 'description']);

function wait(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

async function pollPreviewUntilReady({
    ticker,
    suggestionKey,
    definition,
    token,
    isCurrentPreview,
    onPreview,
    preview,
    attempt = 0,
}) {
    if (!isCurrentPreview() || preview?.history_status !== 'loading' || attempt >= PREVIEW_POLL_ATTEMPTS) {
        return preview;
    }

    await wait(PREVIEW_POLL_DELAY_MS);
    if (!isCurrentPreview()) return preview;

    const nextPreview = await previewCustomMetricSuggestion(ticker, suggestionKey, {
        definition,
    }, token);

    if (!isCurrentPreview()) return nextPreview;
    onPreview(nextPreview);

    return pollPreviewUntilReady({
        ticker,
        suggestionKey,
        definition,
        token,
        isCurrentPreview,
        onPreview,
        preview: nextPreview,
        attempt: attempt + 1,
    });
}

function reducer(state, action) {
    switch (action.type) {
        case 'load-start':
            return {
                ...state,
                suggestions: [],
                suggestionsTicker: action.ticker,
                status: 'loading',
                reviewStatusByKey: {},
                previewStatusByKey: {},
                previewsByKey: {},
                error: null,
            };
        case 'load-success':
            if (action.ticker !== state.suggestionsTicker) {
                return state;
            }
            return {
                ...state,
                status: 'success',
                suggestions: action.suggestions,
                error: null,
            };
        case 'load-error':
            if (action.ticker !== state.suggestionsTicker) {
                return state;
            }
            return { ...state, status: 'error', error: action.error };
        case 'clear-suggestions':
            return {
                ...state,
                suggestions: [],
                suggestionsTicker: '',
                status: 'idle',
                reviewStatusByKey: {},
                previewStatusByKey: {},
                previewsByKey: {},
                error: null,
            };
        case 'scan-start':
            return { ...state, scanStatus: 'loading', error: null, lastScan: null };
        case 'scan-success':
            return {
                ...state,
                scanStatus: 'success',
                lastScan: action.payload,
                error: null,
            };
        case 'scan-error':
            return { ...state, scanStatus: 'error', error: action.error };
        case 'review-start':
            return {
                ...state,
                reviewStatusByKey: {
                    ...state.reviewStatusByKey,
                    [action.key]: 'loading',
                },
                error: null,
            };
        case 'review-success':
            return {
                ...state,
                suggestions: state.suggestions.filter(
                    (suggestion) => suggestion.suggestion_key !== action.key,
                ),
                reviewStatusByKey: {
                    ...state.reviewStatusByKey,
                    [action.key]: 'success',
                },
                error: null,
            };
        case 'review-error':
            return {
                ...state,
                reviewStatusByKey: {
                    ...state.reviewStatusByKey,
                    [action.key]: 'error',
                },
                error: action.error,
            };
        case 'preview-start':
            return {
                ...state,
                previewStatusByKey: {
                    ...state.previewStatusByKey,
                    [action.key]: 'loading',
                },
                error: null,
            };
        case 'preview-success':
            return {
                ...state,
                previewStatusByKey: {
                    ...state.previewStatusByKey,
                    [action.key]: action.preview?.history_status === 'loading' ? 'loading' : 'success',
                },
                previewsByKey: {
                    ...state.previewsByKey,
                    [action.key]: action.preview,
                },
                error: null,
            };
        case 'preview-error':
            return {
                ...state,
                previewStatusByKey: {
                    ...state.previewStatusByKey,
                    [action.key]: 'error',
                },
                error: action.error,
            };
        case 'clear-preview': {
            const nextStatuses = { ...state.previewStatusByKey };
            const nextPreviews = { ...state.previewsByKey };
            delete nextStatuses[action.key];
            delete nextPreviews[action.key];
            return {
                ...state,
                previewStatusByKey: nextStatuses,
                previewsByKey: nextPreviews,
            };
        }
        default:
            return state;
    }
}

function normalizeTicker(value) {
    return String(value || '').trim().toUpperCase();
}

function formatDate(value) {
    if (!value) return 'Unknown date';
    const parsed = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

function formatValue(value, unit) {
    if (value === null || value === undefined || value === '') return 'n/a';
    const number = Number(value);
    const formatted = Number.isNaN(number)
        ? String(value)
        : VALUE_FORMATTER.format(number);
    return unit ? `${formatted} ${unit}` : formatted;
}

function buildInitialDrafts(suggestions) {
    return suggestions.reduce((acc, suggestion) => {
        acc[suggestion.suggestion_key] = {
            display_name: suggestion.display_name || '',
            metric_key: suggestion.metric_key || '',
            unit: suggestion.unit || '',
            row_label: suggestion.row_label || suggestion.display_name || '',
            description: suggestion.description || '',
            is_public: true,
            public_noindex: false,
            public_slug: '',
            seo_title: '',
            seo_description: '',
            rejection_reason: '',
        };
        return acc;
    }, {});
}

function MetricPreviewChart({ values, unit }) {
    const gridStroke = useColorModeValue('#E2E8F0', '#2D3748');
    const tooltipBg = useColorModeValue('white', 'gray.800');
    const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
    const chartData = useMemo(
        () => values.map((value) => ({
            date: value.period_end,
            value: Number(value.value),
            filingDate: value.filing_date,
        })),
        [values],
    );

    if (chartData.length < 2) {
        return null;
    }

    return (
        <Box h="260px" borderWidth="1px" borderColor={tooltipBorder} borderRadius="md" p={3}>
            <Suspense fallback={<Flex h="100%" align="center" justify="center"><Spinner size="sm" /></Flex>}>
                <MetricPreviewRechartsChart
                    chartData={chartData}
                    gridStroke={gridStroke}
                    tooltipBg={tooltipBg}
                    tooltipBorder={tooltipBorder}
                    unit={unit}
                />
            </Suspense>
        </Box>
    );
}

function MetricPreviewTable({ values, unit }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg = useColorModeValue('gray.50', 'gray.800');
    const muted = useColorModeValue('gray.600', 'gray.300');

    if (!values.length) {
        return (
            <Text color={muted} fontSize="sm">
                No historical values were found for this definition.
            </Text>
        );
    }

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" overflowX="auto">
            <Box as="table" w="100%" fontSize="sm">
                <Box as="thead" bg={headerBg}>
                    <Box as="tr">
                        <Box as="th" textAlign="left" p={2}>Period</Box>
                        <Box as="th" textAlign="right" p={2}>Value</Box>
                        <Box as="th" textAlign="left" p={2}>Form</Box>
                        <Box as="th" textAlign="left" p={2}>Filed</Box>
                        <Box as="th" textAlign="left" p={2}>Method</Box>
                        <Box as="th" textAlign="right" p={2}>Confidence</Box>
                        <Box as="th" textAlign="left" p={2}>Source</Box>
                    </Box>
                </Box>
                <Box as="tbody">
                    {values.map((value, index) => (
                        <Box
                            as="tr"
                            key={`${value.period_end}-${value.accession_number || index}`}
                            borderTopWidth="1px"
                            borderColor={borderColor}
                        >
                            <Box as="td" p={2}>{formatDate(value.period_end)}</Box>
                            <Box as="td" p={2} textAlign="right">{formatValue(value.value, unit)}</Box>
                            <Box as="td" p={2}>{value.form || 'n/a'}</Box>
                            <Box as="td" p={2}>{formatDate(value.filing_date)}</Box>
                            <Box as="td" p={2}>{value.extraction_method || 'n/a'}</Box>
                            <Box as="td" p={2} textAlign="right">
                                {Math.round(Number(value.confidence || 0) * 100)}%
                            </Box>
                            <Box as="td" p={2}>
                                {value.source_url ? (
                                    <Link href={value.source_url} target="_blank" rel="noopener noreferrer" color="teal.600">
                                        Filing
                                    </Link>
                                ) : (
                                    <Text as="span" color={muted}>n/a</Text>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

function MetricSuggestionCard({
    suggestion,
    draft,
    onDraftChange,
    onPreview,
    onApprove,
    onReject,
    reviewStatus,
    previewStatus,
    preview,
}) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const cardBg = useColorModeValue('white', 'gray.900');
    const muted = useColorModeValue('gray.600', 'gray.300');
    const sampleValues = suggestion.sample_values || [];
    const isReviewing = reviewStatus === 'loading';
    const isPreviewing = previewStatus === 'loading';
    const previewValues = preview?.values || [];
    const canApprove = Boolean(preview) && (
        preview.history_status ? preview.history_status === 'complete' : true
    );

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={cardBg} p={4}>
            <Stack gap={4}>
                <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap">
                    <Box>
                        <Flex align="center" gap={2} wrap="wrap">
                            <Heading size="sm">{suggestion.display_name}</Heading>
                            <Badge colorPalette={suggestion.detection_method === 'table_scan' ? 'green' : 'blue'}>
                                {suggestion.detection_method}
                            </Badge>
                            <Badge variant="outline">
                                {Math.round(Number(suggestion.confidence || 0) * 100)}% confidence
                            </Badge>
                        </Flex>
                        <Text color={muted} fontSize="sm" mt={1}>
                            {suggestion.ticker} / {suggestion.metric_key}
                        </Text>
                    </Box>
                    {suggestion.source_url && (
                        <Button
                            as={Link}
                            href={suggestion.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="sm"
                            variant="outline"
                        >
                            <FiExternalLink />
                            Source filing
                        </Button>
                    )}
                </Flex>

                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Display name</Text>
                        <Input
                            value={draft.display_name}
                            onChange={(event) => onDraftChange('display_name', event.target.value)}
                        />
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Metric key</Text>
                        <Input
                            value={draft.metric_key}
                            onChange={(event) => onDraftChange('metric_key', event.target.value)}
                        />
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Unit</Text>
                        <Input
                            value={draft.unit}
                            onChange={(event) => onDraftChange('unit', event.target.value)}
                        />
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Row label</Text>
                        <Input
                            value={draft.row_label}
                            onChange={(event) => onDraftChange('row_label', event.target.value)}
                        />
                    </Box>
                </Grid>

                <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={1}>Description</Text>
                    <Textarea
                        value={draft.description}
                        onChange={(event) => onDraftChange('description', event.target.value)}
                        rows={2}
                    />
                </Box>

                <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                    <Stack gap={3}>
                        <Flex justify="space-between" align="center" gap={3} wrap="wrap">
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold">Public page publishing</Text>
                                <Text color={muted} fontSize="sm">
                                    Approved public metrics can appear in public APIs, SEO pages, and the sitemap.
                                </Text>
                            </Box>
                            <Switch.Root
                                size="sm"
                                checked={draft.is_public !== false}
                                onCheckedChange={(event) => onDraftChange('is_public', event.checked)}
                            >
                                <Switch.HiddenInput aria-label="Publish metric publicly" />
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Switch.Label fontSize="sm">Public</Switch.Label>
                            </Switch.Root>
                        </Flex>

                        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                            <Flex justify="space-between" align="center" gap={3} borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3}>
                                <Box>
                                    <Text fontSize="sm" fontWeight="semibold">Noindex</Text>
                                    <Text color={muted} fontSize="sm">
                                        Keep the direct page available but omit it from SEO indexes and sitemap entries.
                                    </Text>
                                </Box>
                                <Switch.Root
                                    size="sm"
                                    checked={draft.public_noindex === true}
                                    onCheckedChange={(event) => onDraftChange('public_noindex', event.checked)}
                                    disabled={draft.is_public === false}
                                >
                                    <Switch.HiddenInput aria-label="Mark public metric noindex" />
                                    <Switch.Control>
                                        <Switch.Thumb />
                                    </Switch.Control>
                                </Switch.Root>
                            </Flex>
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={1}>Public slug</Text>
                                <Input
                                    value={draft.public_slug}
                                    onChange={(event) => onDraftChange('public_slug', event.target.value)}
                                    placeholder="Optional; defaults from metric key"
                                />
                            </Box>
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={1}>SEO title</Text>
                                <Input
                                    value={draft.seo_title}
                                    onChange={(event) => onDraftChange('seo_title', event.target.value)}
                                    placeholder="Optional"
                                />
                            </Box>
                        </Grid>

                        <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>SEO description</Text>
                            <Textarea
                                value={draft.seo_description}
                                onChange={(event) => onDraftChange('seo_description', event.target.value)}
                                placeholder="Optional"
                                rows={2}
                            />
                        </Box>
                    </Stack>
                </Box>

                <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>Sample values</Text>
                    {sampleValues.length ? (
                        <Flex gap={2} wrap="wrap">
                            {sampleValues.slice(0, 6).map((sample) => (
                                <Badge key={`${sample.period_end}-${sample.value}`} variant="subtle" colorPalette="gray">
                                    {formatDate(sample.period_end)}: {formatValue(sample.value, suggestion.unit)}
                                </Badge>
                            ))}
                        </Flex>
                    ) : (
                        <Text color={muted} fontSize="sm">No sample values captured.</Text>
                    )}
                </Box>

                {preview && (
                    <Stack gap={3}>
                        <Stack gap={1}>
                            <Text fontWeight="semibold">Historical extraction preview</Text>
                            <Text color={muted} fontSize="sm">
                                {preview.value_count || 0} value(s) found. Review these before adding them to the database.
                            </Text>
                            {preview.history_status === 'loading' && (
                                <Text color={muted} fontSize="sm">
                                    Full history scan is still running.
                                </Text>
                            )}
                        </Stack>
                        {(preview.warnings || []).map((warning) => (
                            <Alert.Root key={warning} status="warning" borderRadius="md">
                                <Alert.Indicator />
                                <Alert.Title>{warning}</Alert.Title>
                            </Alert.Root>
                        ))}
                        <MetricPreviewChart values={previewValues} unit={draft.unit || suggestion.unit} />
                        <MetricPreviewTable values={previewValues} unit={draft.unit || suggestion.unit} />
                    </Stack>
                )}

                <Grid templateColumns={{ base: '1fr', md: '1fr auto auto auto' }} gap={3} alignItems="end">
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Rejection reason</Text>
                        <Input
                            value={draft.rejection_reason}
                            onChange={(event) => onDraftChange('rejection_reason', event.target.value)}
                            placeholder="Optional"
                        />
                    </Box>
                    <Button
                        colorPalette="blue"
                        variant="outline"
                        onClick={onPreview}
                        loading={isPreviewing}
                        disabled={isPreviewing || isReviewing}
                    >
                        <FiSearch />
                        Find history
                    </Button>
                    <Button
                        colorPalette="green"
                        onClick={onApprove}
                        loading={isReviewing}
                        disabled={isReviewing || isPreviewing || !canApprove}
                    >
                        <FiCheck />
                        Add to database
                    </Button>
                    <Button
                        colorPalette="red"
                        variant="outline"
                        onClick={onReject}
                        loading={isReviewing}
                        disabled={isReviewing}
                    >
                        <FiX />
                        Reject
                    </Button>
                </Grid>
            </Stack>
        </Box>
    );
}

export default function CustomMetricsAdmin() {
    const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
    const [tickerInput, setTickerInput] = useState(DEFAULT_TICKER);
    const [limit, setLimit] = useState(4);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [drafts, setDrafts] = useState({});
    const previewRequestIds = useRef({});
    const loadRequestId = useRef(0);
    const pageBg = useColorModeValue('gray.50', 'gray.950');
    const panelBg = useColorModeValue('white', 'gray.900');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.600', 'gray.300');

    const ticker = normalizeTicker(tickerInput);
    const loading = state.status === 'loading';
    const scanning = state.scanStatus === 'loading';

    const getAdminToken = useCallback(async () => {
        try {
            return await getAccessTokenSilently({ detailedResponse: false });
        } catch (err) {
            if (isRecoverableAuthError(err)) {
                await loginWithRedirect(getLoginRedirectOptions());
                return null;
            }
            throw err;
        }
    }, [getAccessTokenSilently, loginWithRedirect]);

    const loadSuggestionsForTicker = useCallback(async (targetTicker) => {
        if (!targetTicker) {
            loadRequestId.current += 1;
            setDrafts({});
            dispatch({ type: 'clear-suggestions' });
            return;
        }

        const requestId = loadRequestId.current + 1;
        loadRequestId.current = requestId;
        dispatch({ type: 'load-start', ticker: targetTicker });
        try {
            const token = await getAdminToken();
            if (!token) return;
            const payload = await getCustomMetricSuggestions(targetTicker, token);
            if (loadRequestId.current !== requestId) return;
            const suggestions = payload?.suggestions || [];
            dispatch({ type: 'load-success', ticker: targetTicker, suggestions });
            setDrafts(buildInitialDrafts(suggestions));
        } catch (err) {
            if (loadRequestId.current !== requestId) return;
            dispatch({
                type: 'load-error',
                ticker: targetTicker,
                error: err?.message || 'Unable to load custom metric suggestions.',
            });
        }
    }, [getAdminToken]);

    const loadSuggestions = useCallback(() => loadSuggestionsForTicker(ticker), [loadSuggestionsForTicker, ticker]);

    useEffect(() => {
        loadSuggestionsForTicker(ticker);
    }, [loadSuggestionsForTicker, ticker]);

    const handleDraftChange = (suggestionKey, field, value) => {
        setDrafts((current) => ({
            ...current,
            [suggestionKey]: {
                ...(current[suggestionKey] || {}),
                [field]: value,
            },
        }));
        if (PREVIEW_DEFINITION_FIELDS.has(field)) {
            previewRequestIds.current[suggestionKey] = (previewRequestIds.current[suggestionKey] || 0) + 1;
            dispatch({ type: 'clear-preview', key: suggestionKey });
        }
    };

    const handleScan = async () => {
        if (!ticker) return;
        dispatch({ type: 'scan-start' });
        try {
            const token = await getAdminToken();
            if (!token) return;
            const payload = await scanCustomMetricSuggestions({ ticker, limit }, token);
            dispatch({ type: 'scan-success', payload });
            void loadSuggestionsForTicker(ticker);
        } catch (err) {
            dispatch({
                type: 'scan-error',
                error: err?.message || 'Unable to scan SEC filings for custom metrics.',
            });
        }
    };

    const handlePreview = async (suggestion) => {
        const suggestionKey = suggestion.suggestion_key;
        const previewRequestId = (previewRequestIds.current[suggestionKey] || 0) + 1;
        previewRequestIds.current[suggestionKey] = previewRequestId;
        const isCurrentPreview = () => previewRequestIds.current[suggestionKey] === previewRequestId;
        const draft = drafts[suggestion.suggestion_key] || {};
        const definition = {
            display_name: draft.display_name,
            metric_key: draft.metric_key,
            unit: draft.unit,
            row_label: draft.row_label,
            description: draft.description,
            example_points: suggestion.example_points || {},
        };
        dispatch({ type: 'preview-start', key: suggestionKey });
        try {
            const token = await getAdminToken();
            if (!token) return;
            if (!isCurrentPreview()) return;
            let preview = await previewCustomMetricSuggestion(ticker, suggestionKey, {
                definition,
                force: true,
            }, token);
            if (isCurrentPreview()) {
                dispatch({ type: 'preview-success', key: suggestionKey, preview });
            }
            if (isCurrentPreview() && preview?.history_status === 'loading') {
                preview = await pollPreviewUntilReady({
                    ticker,
                    suggestionKey,
                    definition,
                    token,
                    isCurrentPreview,
                    onPreview: (nextPreview) => {
                        dispatch({ type: 'preview-success', key: suggestionKey, preview: nextPreview });
                    },
                    preview,
                });
                if (isCurrentPreview() && preview?.history_status === 'loading') {
                    dispatch({
                        type: 'preview-error',
                        key: suggestionKey,
                        error: 'Full history scan is still running. Try again in a moment.',
                    });
                }
            }
        } catch (err) {
            if (!isCurrentPreview()) return;
            dispatch({
                type: 'preview-error',
                key: suggestionKey,
                error: err?.message || 'Unable to preview historical values.',
            });
        }
    };

    const handleReview = async (suggestion, action) => {
        const draft = drafts[suggestion.suggestion_key] || {};
        const preview = state.previewsByKey[suggestion.suggestion_key];
        dispatch({ type: 'review-start', key: suggestion.suggestion_key });
        try {
            const token = await getAdminToken();
            if (!token) return;
            await reviewCustomMetricSuggestion(ticker, suggestion.suggestion_key, {
                action,
                reviewedBy: user?.email || 'dcf-teacher-admin',
                rejectionReason: draft.rejection_reason,
                definition: action === 'approve'
                    ? {
                        display_name: draft.display_name,
                        metric_key: draft.metric_key,
                        unit: draft.unit,
                        row_label: draft.row_label,
                        description: draft.description,
                        example_points: suggestion.example_points || {},
                        is_public: draft.is_public !== false,
                        public_noindex: draft.public_noindex === true,
                        public_slug: draft.public_slug,
                        seo_title: draft.seo_title,
                        seo_description: draft.seo_description,
                    }
                    : {},
                previewValues: action === 'approve' ? preview?.values || [] : [],
            }, token);
            dispatch({ type: 'review-success', key: suggestion.suggestion_key });
        } catch (err) {
            dispatch({
                type: 'review-error',
                key: suggestion.suggestion_key,
                error: err?.message || `Unable to ${action} suggestion.`,
            });
        }
    };

    return (
        <Box bg={pageBg} minH="calc(100vh - var(--nav-h))" py={8} px={{ base: 4, md: 8 }}>
            <Stack maxW="1180px" mx="auto" gap={6}>
                <Box>
                    <Heading size="lg">Custom Metrics Admin</Heading>
                    <Text color={muted} mt={2}>
                        Scan SEC filings for company KPIs, review pending candidates, and approve the metrics
                        DCF Teacher should track going forward.
                    </Text>
                </Box>

                <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={panelBg} p={4}>
                    <Grid templateColumns={{ base: '1fr', md: 'minmax(160px, 220px) 130px auto auto' }} gap={3} alignItems="end">
                        <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>Ticker</Text>
                            <Input
                                value={tickerInput}
                                onChange={(event) => setTickerInput(event.target.value)}
                                placeholder="HOOD"
                            />
                        </Box>
                        <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>Filings</Text>
                            <NativeSelect.Root>
                                <NativeSelect.Field
                                    value={limit}
                                    onChange={(event) => setLimit(Number(event.target.value))}
                                >
                                    {[2, 4, 6, 8, 10].map((value) => (
                                        <option key={value} value={value}>{value}</option>
                                    ))}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                            </NativeSelect.Root>
                        </Box>
                        <Button onClick={handleScan} colorPalette="teal" loading={scanning} disabled={!ticker || scanning}>
                            <FiSearch />
                            Scan filings
                        </Button>
                        <Button onClick={loadSuggestions} variant="outline" loading={loading} disabled={!ticker || loading}>
                            <FiRefreshCw />
                            Refresh queue
                        </Button>
                    </Grid>
                    {state.lastScan && (
                        <Text color={muted} fontSize="sm" mt={3}>
                            Last scan found {state.lastScan.suggestion_count || 0} candidate(s) and upserted{' '}
                            {state.lastScan.upserted_count || 0} pending suggestion(s).
                        </Text>
                    )}
                </Box>

                {state.error && (
                    <Alert.Root status="error" borderRadius="md">
                        <Alert.Indicator />
                        <Alert.Title>{state.error}</Alert.Title>
                    </Alert.Root>
                )}

                {loading ? (
                    <Flex justify="center" py={12}>
                        <Spinner size="lg" />
                    </Flex>
                ) : state.suggestions.length ? (
                    <Stack gap={4}>
                        {state.suggestions.map((suggestion) => (
                            <MetricSuggestionCard
                                key={suggestion.suggestion_key}
                                suggestion={suggestion}
                                draft={drafts[suggestion.suggestion_key] || {}}
                                reviewStatus={state.reviewStatusByKey[suggestion.suggestion_key]}
                                previewStatus={state.previewStatusByKey[suggestion.suggestion_key]}
                                preview={state.previewsByKey[suggestion.suggestion_key]}
                                onDraftChange={(field, value) => handleDraftChange(suggestion.suggestion_key, field, value)}
                                onPreview={() => handlePreview(suggestion)}
                                onApprove={() => handleReview(suggestion, 'approve')}
                                onReject={() => handleReview(suggestion, 'reject')}
                            />
                        ))}
                    </Stack>
                ) : (
                    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={panelBg} p={6}>
                        <Heading size="sm" mb={2}>No pending suggestions</Heading>
                        <Text color={muted}>
                            Scan recent filings for a ticker to populate this queue, or switch to another ticker.
                        </Text>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
